"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  return (
    "#" +
    [r, g, b]
      .map((n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0"))
      .join("")
  );
}

function rgbToHsv(r: number, g: number, b: number) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

function hsvToRgb(h: number, s: number, v: number) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

function hsvToHex(h: number, s: number, v: number) {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg width="21" height="22" fill="none" viewBox="0 0 21 22" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.57 4.3c.21-.1.46-.13.7-.13 2.51 0 4.92 2.26 5.98 4.72.64 1.48-.03 2.24-.18 3l-7.12 7.77a3.65 3.65 0 0 1-1.91 1.1l-5.49 1.21c-.41.1-.84-.03-1.15-.33a1.34 1.34 0 0 1-.39-1.15l.88-5.92c.09-.62.36-1.19.77-1.64L9.57 4.3zm2.15-2.33.35-.39a4.81 4.81 0 0 1 6.92-.21 5.12 5.12 0 0 1 .21 7.1l-.46.51c-.82-3.17-3.73-6.26-7.02-7.01z"
        fill="currentColor"
      />
    </svg>
  );
}

export function ColorPickerTrigger({
  selected,
  onClick,
  buttonRef,
}: {
  selected: boolean;
  onClick: () => void;
  buttonRef: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label="Custom color"
      title="Custom color"
      aria-pressed={selected}
      onClick={onClick}
      className={`relative shrink-0 w-12 h-12 md:w-12 md:h-12 rounded-full border border-black/10 shadow-sm ring-2 transition-[box-shadow] duration-200 flex items-center justify-center bg-white text-gray-700 hover:ring-primary focus-visible:ring-primary ${
        selected ? "ring-primary" : "ring-transparent"
      }`}
    >
      <PencilIcon className="w-5 h-5" />
    </button>
  );
}

export function SketchColorPopover({
  anchorRef,
  value,
  onChange,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  value: string;
  onChange: (hex: string) => void;
  onClose: () => void;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  const satRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [hexText, setHexText] = useState(value.replace("#", "").toUpperCase());

  const rgb = hexToRgb(value) || { r: 89, g: 86, b: 233 };
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
  const hsvRef = useRef(hsv);
  hsvRef.current = hsv;

  useEffect(() => {
    setHexText(value.replace("#", "").toUpperCase());
  }, [value]);

  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = 220;
      const height = 248;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
      if (left < 8) left = 8;
      let top = rect.bottom + 8;
      if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 8);
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [anchorRef, onClose]);

  const emitHsv = (next: { h: number; s: number; v: number }) => {
    hsvRef.current = next;
    onChange(hsvToHex(next.h, next.s, next.v));
  };

  const dragSat = (clientX: number, clientY: number) => {
    const el = satRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const s = clamp((clientX - rect.left) / rect.width, 0, 1);
    const v = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
    emitHsv({ ...hsvRef.current, s, v });
  };

  const dragHue = (clientX: number) => {
    const el = hueRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const h = clamp((clientX - rect.left) / rect.width, 0, 1) * 359.99;
    emitHsv({ ...hsvRef.current, h });
  };

  const bindDrag = (
    start: (e: React.PointerEvent<HTMLDivElement>) => void
  ) => ({
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      start(e);
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      start(e);
    },
  });

  if (typeof document === "undefined") return null;

  const hueColor = hsvToHex(hsv.h, 1, 1);

  return createPortal(
    <div
      ref={popRef}
      className="fixed z-[80] w-[220px] rounded-[7px] bg-white p-4 shadow-[0_4px_6px_-2px_rgba(16,24,40,0.03),0_12px_16px_-4px_rgba(16,24,40,0.08)]"
      style={{ top: pos.top, left: pos.left }}
      role="dialog"
      aria-label="Custom color"
    >
      <div
        ref={satRef}
        className="relative w-full rounded overflow-hidden cursor-crosshair touch-none"
        style={{ paddingBottom: "50%", backgroundColor: hueColor }}
        {...bindDrag((e) => dragSat(e.clientX, e.clientY))}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #fff, transparent)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #000, transparent)" }} />
        <span
          className="absolute w-3 h-3 rounded-full border-[5px] border-white pointer-events-none"
          style={{
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            boxShadow: "0 0 2px rgb(0 0 0 / 60%)",
            transform: "translate(-12px, -12px)",
          }}
        />
      </div>

      <div
        ref={hueRef}
        className="relative h-[14px] mt-3 mb-3 rounded-lg cursor-pointer touch-none"
        style={{
          background:
            "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
        }}
        {...bindDrag((e) => dragHue(e.clientX))}
      >
        <span
          className="absolute top-1/2 w-[22px] h-[22px] rounded-full border-[5px] border-white pointer-events-none bg-transparent"
          style={{
            left: `${(hsv.h / 360) * 100}%`,
            transform: "translate(-11px, -50%)",
            boxShadow: "0 0 2px rgb(0 0 0 / 40%)",
          }}
        />
      </div>

      <div className="relative">
        <span className="absolute left-[13px] top-[6px] text-[13px] font-semibold leading-[22px] tracking-[0.25px] text-gray-800 pointer-events-none">
          #
        </span>
        <input
          aria-label="Hex color"
          value={hexText}
          maxLength={6}
          spellCheck={false}
          className="box-border h-8 w-full rounded-md border border-gray-200 bg-white pl-[21px] pr-3 text-[13px] font-semibold leading-[22px] tracking-[0.25px] text-gray-800 uppercase outline-none focus:border-primary"
          onChange={(e) => {
            const next = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
            setHexText(next.toUpperCase());
            if (next.length === 6) onChange(`#${next}`);
          }}
        />
      </div>
    </div>,
    document.body
  );
}
